import { SessionLmsAction, SessionStatus, SettingScopesEnum } from "@simbelapp/order-sdk";
import type { ItemType as ItemTypeSDK } from "@simbelapp/order-sdk";
import { useNotificationsStore } from "~/store/notifications/notifications.store";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { useTrainingsPlansStore } from "~/store/training-plan/trainings-plans.store";
import { ActionTypeEnum, typeNotications } from "~/utils/enums/notifications.enums";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { DateChoiceStatusEnum } from "~/utils/enums/date-choice.enums";
import { SessionStatusEnum } from "~/utils/enums/session-details-enums";
import { TagCategoryEnum } from "~/utils/enums/tags.enums";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";
import type { TrainingPlanIndividual } from "~/utils/interfaces/sessions-interfaces";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useCustomFieldsStore } from "~/store/settings/custom-fields.store";
import type { CustomField } from "~/utils/interfaces/custom-fields-interfaces";
import { ItemType } from "~/utils/enums/common.enums";
import { RolesEnums } from "~/utils/enums/roles.enums";
import { SessionNotificationDeliveryEnum } from "~/utils/enums/session-notifications.enums";
import type { AttendeesDeliveryChoice } from "~/utils/helpers/session-notifications/delivery-choice";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import { useSessionDetailsDocumentsStore } from "~/store/sessions/session-details-documents-store";
import { useSessionLmsRules } from "~/composables/sessions/useSessionLmsRules";

export const useSessions = () => {
  const notificationStore = useNotificationsStore();
  const { notifyModal, createSession, type, actionType, deliveryChoice } = storeToRefs(notificationStore);
  const createProjectCoreStore = useCreateProjectStore();
  const sessionDetailsStore = useSessionDetailsStore();
  const { sessionDetails } = storeToRefs(sessionDetailsStore);
  const trainingsPlansStore = useTrainingsPlansStore();
  const { showEditTagModal, categoryTag, showVisibilitySessionModal } = storeToRefs(trainingsPlansStore);
  const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
  const {
    sessionDetailsAttendees,
    showUnscheduleAttendeesModal,
    showDeleteAttendeesModal,
    sessionAttendeesMultiSelect,
    sessionAttendeesSelectAll,
    showExceedNotificationsModal,
    deliveryChoiceAction,
    actionExceededDirectNotify,
  } = storeToRefs(sessionDetailsAttendeesStore);
  const { isLmsActionDisabled } = useSessionLmsRules();
  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Participant",
      sort: true,
      key: "lastname",
      minWidthPx: 140,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Formation",
      sort: true,
      key: "training",
      minWidthPx: 240,
      maxWidthFr: 4,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Prestataire",
      sort: true,
      key: "training_partner",
      minWidthPx: 140,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Priorité",
      sort: false,
      key: "manager_priority",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Indication",
      sort: true,
      key: "designation",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Statut",
      sort: true,
      key: "status",
      minWidthPx: 165,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Motif d'annulation",
      sort: true,
      key: "cancel_reason",
      minWidthPx: 165,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Début",
      sort: true,
      key: "start_date",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Fin",
      sort: true,
      key: "end_date",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Durée",
      sort: true,
      key: "training_duration",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Lieu",
      sort: true,
      key: "location",
      minWidthPx: 214,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Type",
      sort: true,
      key: "type",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Format",
      sort: true,
      key: "format",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Prix (HT)",
      sort: true,
      key: "price_excl_tax",
      minWidthPx: 190,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Tag formation",
      sort: false,
      key: "training_tag",
      minWidthPx: 190,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Tag session",
      sort: false,
      key: "session_tag",
      minWidthPx: 190,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Coûts salaires",
      sort: true,
      key: "salary_cost",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Frais annexes",
      sort: true,
      key: "other_costs",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Habilitation",
      sort: true,
      key: "mandatory",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
    {
      name: "Code session",
      sort: false,
      key: "session_code",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanIndividualTab,
    },
  ]);

  const sessionHeaders = useState<IIndexHeader[]>(() => [
    {
      name: "Code session",
      sort: false,
      key: "session_code",
      minWidthPx: 140,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Formation",
      sort: true,
      key: "training_title",
      minWidthPx: 365,
      maxWidthFr: 4,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Prestataire",
      sort: true,
      key: "training_partner",
      minWidthPx: 215,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Indication",
      sort: true,
      key: "designation",
      minWidthPx: 120,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Début",
      sort: true,
      key: "start_date",
      minWidthPx: 90,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Fin",
      sort: true,
      key: "end_date",
      minWidthPx: 90,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "",
      sort: false,
      key: "alerts",
      minWidthPx: 30,
      maxWidthFr: 0.2,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Statut",
      sort: true,
      key: "session_status",
      minWidthPx: 200,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Type",
      sort: true,
      key: "session_type",
      minWidthPx: 100,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Participants",
      sort: true,
      key: "nb_guests",
      minWidthPx: 132,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Lieu",
      sort: true,
      key: "location",
      minWidthPx: 214,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Tâches restantes",
      sort: true,
      key: "remaining_tasks",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Prix (HT)",
      sort: true,
      key: "price_excl_tax",
      minWidthPx: 214,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Tag formation",
      sort: false,
      key: "training_tag",
      minWidthPx: 170,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
    {
      name: "Tag session",
      sort: false,
      key: "session_tag",
      minWidthPx: 180,
      maxWidthFr: 1,
      visible: false,
      scope: SettingScopesEnum.TrainingPlanGroupTab,
    },
  ]);

  const sessionAttendeesHeaders = (customfield: boolean, isManager2: boolean): IIndexHeader[] => {
    let customFields: CustomField[] = [];
    if (customfield) {
      const customFieldsStore = useCustomFieldsStore();
      customFields = customFieldsStore.customFields;
    }

    const headers: IIndexHeader[] = [
      {
        name: "Participant",
        visible: true,
        sort: true,
        minWidthPx: 120,
        maxWidthFr: 1,
        key: "lastname",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Mail",
        visible: true,
        sort: true,
        minWidthPx: 110,
        maxWidthFr: 1,
        key: "email",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Entité légale",
        visible: true,
        sort: true,
        minWidthPx: 90,
        maxWidthFr: 1,
        key: "legalEntity",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Equipe",
        visible: true,
        sort: true,
        minWidthPx: 90,
        maxWidthFr: 1,
        key: "team",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Poste",
        visible: false,
        sort: true,
        minWidthPx: 120,
        maxWidthFr: 1,
        key: "job",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "",
        visible: true,
        sort: false,
        minWidthPx: 30,
        maxWidthFr: 0.3,
        key: "alerts",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Statut",
        visible: true,
        sort: true,
        minWidthPx: 110,
        maxWidthFr: 1,
        key: "status",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Convoqué le",
        visible: true,
        sort: false,
        minWidthPx: 100,
        maxWidthFr: 1,
        key: "summonedAt",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Début",
        visible: false,
        sort: true,
        minWidthPx: 100,
        maxWidthFr: 1,
        key: "startDate",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Fin",
        visible: false,
        sort: true,
        minWidthPx: 100,
        maxWidthFr: 1,
        key: "endDate",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Invitation",
        visible: true,
        sort: false,
        minWidthPx: 90,
        maxWidthFr: 1,
        key: "icsStatus",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Progression",
        visible: true,
        sort: true,
        minWidthPx: 100,
        maxWidthFr: 1,
        key: "completionRate",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Durée",
        visible: true,
        sort: true,
        minWidthPx: 90,
        maxWidthFr: 1,
        key: "attendedHours",
        scope: SettingScopesEnum.SessionAttendees,
      },

      {
        name: "Score",
        visible: true,
        sort: true,
        minWidthPx: 90,
        maxWidthFr: 1,
        key: "score",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Site",
        visible: false,
        sort: true,
        minWidthPx: 110,
        maxWidthFr: 1,
        key: "site",
        scope: SettingScopesEnum.SessionAttendees,
      },
      {
        name: "Manager",
        visible: false,
        sort: true,
        minWidthPx: 110,
        maxWidthFr: 1,
        key: "manager",
        scope: SettingScopesEnum.SessionAttendees,
      },
      ...(isManager2
        ? [
            {
              name: "Manager n+2",
              visible: false,
              sort: false,
              minWidthPx: 110,
              maxWidthFr: 1,
              key: "manager2",
              scope: SettingScopesEnum.SessionAttendees,
            },
          ]
        : []),
      {
        name: "Date de début de contrat",
        visible: false,
        sort: true,
        minWidthPx: 110,
        maxWidthFr: 1,
        key: "contractStartDate",
        scope: SettingScopesEnum.SessionAttendees,
      },
      ...(customfield
        ? customFields.map((field) => ({
            name: field.name,
            visible: false,
            sort: false,
            minWidthPx: 120,
            maxWidthFr: 1,
            key: field.technicalName as string,
            scope: SettingScopesEnum.SessionAttendees,
          }))
        : []),
    ];

    return headers;
  };

  function sessionStatusMapping(value: SessionStatus): { name: string; value: string; type: string } | undefined {
    return [
      {
        name: SessionStatus.PlanningInProgress,
        value: "Planification en cours",
        type: "light_orange",
      },
      {
        name: SessionStatus.Summoned,
        value: "Convoquée",
        type: "light_blue",
      },
      {
        name: SessionStatus.SessionInProgress,
        value: "Formation en cours",
        type: "light_blue",
      },
      {
        name: SessionStatus.SessionBookingsToEnd,
        value: "Non-déclarée terminée",
        type: "light_orange",
      },
      {
        name: SessionStatus.SessionBookingsEnded,
        value: "Déclarée terminée",
        type: "light_blue",
      },
    ].find((x) => x.name === value);
  }

  function statusMap(value: string): { name: string; value: string; type: string } | undefined {
    return [
      {
        name: "pending_employee",
        value: "Invitation envoyée",
        type: "light_orange",
      },
      {
        name: "pending_provider",
        value: "En attente prestataire",
        type: "light_red",
      },
      {
        name: "pending_invitation",
        value: "En attente invitation",
        type: "light_red",
      },
      {
        name: "registered",
        value: "Inscrit",
        type: "light_green",
      },
      {
        name: "in_progress",
        value: "En cours",
        type: "light_green",
      },
      {
        name: "completed",
        value: "Terminé",
        type: "green",
      },
      {
        name: "no_show",
        value: "Absent",
        type: "grey",
      },
      {
        name: "canceled",
        value: "Déplanifié",
        type: "light_red",
      },
      {
        name: "not_completed",
        value: "Absent",
        type: "light_grey",
      },
      {
        name: "not_present",
        value: "Absent",
        type: "light_grey",
      },
      {
        name: "on_hold",
        value: "En attente",
        type: "grey",
      },
      {
        name: "pending_provider", // todo: what to map with ?
        value: "pending_provider",
        type: "grey",
      },
      {
        name: "to_complete",
        value: "À compléter",
        type: "light_purple",
      },
      {
        name: "to_validate",
        value: "À valider",
        type: "light_blue",
      },
      // "Date choice" statuses: a confirmed registration, not yet attached to a session.
      // Amber / grey badges, never merged into a single entry (AC-SUIVI.3).
      {
        name: DateChoiceStatusEnum.PENDING_DATE_CHOICE,
        value: "Choix de date en cours",
        type: ColorsTypeEnum.LIGHT_ORANGE,
      },
      {
        name: DateChoiceStatusEnum.NO_SUITABLE_DATE,
        value: "Aucune date ne convient",
        type: ColorsTypeEnum.GREY,
      },
    ].find((x) => x.name === value);
  }

  function sessionTypeMap(value: string) {
    return [
      {
        name: "individual",
        value: "Inter",
        type: "grey",
        icon: "/icons/generic/indiv.svg",
      },
      {
        name: "group",
        value: "Intra",
        type: "grey",
        icon: "/icons/generic/group.svg",
      },
    ].find((x) => x.name === value);
  }

  function sessionItemFormatMap(value: string) {
    return [
      {
        name: "online",
        value: "eLearning",
        type: "light_orange",
      },
      {
        name: "in_person",
        value: "Classe",
        type: "light_purple",
      },
      {
        name: "mixed",
        value: "Mixte",
        type: "light_purple",
      },
    ].find((x) => x.name === value);
  }
  function addTrainingTag() {
    showEditTagModal.value = true;
    categoryTag.value = TagCategoryEnum.TRAINING_TAG;
  }
  function addSessionTag() {
    showEditTagModal.value = true;
    categoryTag.value = TagCategoryEnum.SESSION_TAG;
  }
  function showVisibility() {
    showVisibilitySessionModal.value = true;
  }
  function getFootersActions(): IFooterActions[] {
    return [
      // Both ask participants or managers first, as the Notifications tab does: the chooser lives in the attendees tab.
      {
        name: "Convoquer",
        logoName: "paper-plane",
        action: () => {
          deliveryChoiceAction.value = ActionTypeEnum.SUMMON;
        },
      },
      {
        name: "Notifier",
        logoName: "notification",
        action: () => {
          deliveryChoiceAction.value = ActionTypeEnum.NOTIFY;
        },
      },

      {
        name: "Changer de session",
        logoName: "create",
        action: () => {
          if (
            createProjectCoreStore.project.users.selected?.length &&
            createProjectCoreStore.project.users.selected.filter((user) => user.status === SessionStatusEnum.COMPLETED)
              .length > 0
          ) {
            createProjectCoreStore.showSessionAlreadyBilledCompleted = true;
          } else {
            // The funnel's Session step asks whether to register directly or delegate the date choice.
            createProjectCoreStore.changeSession(true);
          }
        },
        disabled: isLmsActionDisabled(SessionLmsAction.ChangeSessionForAttendees),
        disabledTooltipText: "Cette action n’est pas disponible pour une session importée d’un LMS",
      },
      {
        name: "Déplanifier",
        logoName: "cancel",
        action: unscheduleUsersFromSession,
        disabled: isLmsActionDisabled(SessionLmsAction.CancelAttendees),
        disabledTooltipText: "Cette action n’est pas disponible pour une session importée d’un LMS",
      },
      {
        name: "Supprimer",
        logoName: "delete_trash",
        action: deleteAttendeesFromSession,
        cancelColor: true,
        disabled: isLmsActionDisabled(SessionLmsAction.RemoveAttendees),
        disabledTooltipText: "Cette action n’est pas disponible pour une session importée d’un LMS",
      },
    ];
  }

  function deliveryEmails(attendees: Record<string, any>[]): string[] {
    return createSession.value.choiceDelivery === RolesEnums.EMPLOYEE
      ? attendees.filter((attendee) => attendee.email).map((attendee) => attendee.email)
      : attendees.filter((attendee) => attendee.managerEmail).map((attendee) => attendee.managerEmail);
  }

  /**
   * Opens the convocation / notification modal. A prompt passes the `recipients` it chose — an empty
   * list included, so a failed fetch sends to nobody rather than to the table's rows; every other
   * entry point uses the attendees table's selection or select-all. `createSession.attendees` is never
   * read as a choice: a prompt answered « Plus tard » leaves its list there, and reusing it convoked
   * only the participants just added. With nobody to send to, it says why instead of opening the modal.
   * Resolves to whether the modal opened.
   */
  async function notify(recipients?: TrainingPlanIndividual.ISessionDetailsAttendees[]): Promise<boolean> {
    const feedback = useFeedback();
    const isConvocation = actionType.value === ActionTypeEnum.SUMMON;

    if (recipients) {
      createSession.value.attendees = recipients;
      createSession.value.emails = deliveryEmails(recipients);
    } else if (sessionAttendeesSelectAll.value || sessionAttendeesMultiSelect.value?.selectedItems?.length) {
      const excludedUserIds = sessionAttendeesMultiSelect.value?.excludedItems?.map((item) => item.userId) ?? [];
      const selectedUserIds = sessionAttendeesMultiSelect.value?.selectedItems?.map((item) => item.userId) ?? [];

      // Single source of truth: attendees-mail-info carries email, manager email, display names and
      // originalId, and applies the selection and the convocation / notification status filter back-side.
      await sessionDetailsStore.fetchSessionAttendeesWithMail(
        sessionAttendeesSelectAll.value
          ? { excludedUserIds, isConvocation }
          : { userIds: selectedUserIds, isConvocation },
      );
      const mailInfoAttendees = sessionDetailsStore.sessionDetailsAttendeesWithMail;

      // `null` is a failed fetch, not a selection without recipients.
      if (!mailInfoAttendees) {
        feedback.error(
          `Impossible de récupérer les participants à ${isConvocation ? "convoquer" : "notifier"}`,
          "small",
        );
        return false;
      }
      // Assigned even when empty: the modal must not fall back on a list left by an earlier flow.
      createSession.value.attendees = mailInfoAttendees.map((attendee) => ({
        userId: attendee.userId,
        originalId: attendee.originalId,
        email: attendee.email,
        managerEmail: attendee.managerEmail,
        displayValue: attendee.displayValue,
        managerDisplayValue: attendee.managerDisplayValue,
        // mail-info exposes displayValue, not firstname/lastname; the notification flow uses displayValue.
        firstname: null,
        lastname: null,
      }));

      createSession.value.emails = deliveryEmails(createSession.value.attendees ?? []);
    } else {
      createSession.value.attendees = [];
      createSession.value.emails = [];
    }

    // A convocation leaves out « Terminé » / « Absent » attendees, so a selection of only those resolves to nobody.
    if (!createSession.value.attendees?.length) {
      feedback.error(
        isConvocation
          ? "Aucun participant à convoquer : les participants au statut « Terminé » ou « Absent » ne peuvent pas être convoqués"
          : "Aucun participant à notifier",
        "small",
      );
      return false;
    }

    // Counted on the resolved recipients: mail-info has already dropped those a convocation skips.
    if (createSession.value.attendees.length >= 200) {
      // Worded by entry point: no list means the table's selection, which the user can narrow.
      actionExceededDirectNotify.value = !recipients;
      showExceedNotificationsModal.value = true;
      return false;
    }

    type.value = typeNotications.session;
    createSession.value.countAttendees = sessionDetailsAttendees.value?.data?.length;
    const sessionDetailsDocumentsStore = useSessionDetailsDocumentsStore();
    const { sessionDocumentsPagination } = storeToRefs(sessionDetailsDocumentsStore);

    if (sessionDetails.value) {
      createSession.value.initDocument.itemId = sessionDetails.value.sessionId;
      createSession.value.initDocument.itemType = ItemType.SESSION;
      createSession.value.initDocument.displayValue = "tous";
      createSession.value.initDocument.attendees = sessionDetailsAttendees.value?.data ?? [];
      await sessionDetailsDocumentsStore.fetchSessionDocumentsWithPagination(sessionDetails.value.sessionId, {
        limit: 10000,
      });
      createSession.value.documents = sessionDocumentsPagination.value?.data ?? [];
    }

    notifyModal.value = true;
    return true;
  }

  async function notifyDelivery(
    choice: AttendeesDeliveryChoice,
    recipients?: TrainingPlanIndividual.ISessionDetailsAttendees[],
    action: ActionTypeEnum = ActionTypeEnum.NOTIFY,
  ): Promise<void> {
    const previousDelivery = createSession.value.choiceDelivery;
    actionType.value = action;
    // Set first: `notify()` resolves the branch's addresses, and the form opens on it.
    createSession.value.choiceDelivery =
      choice === SessionNotificationDeliveryEnum.MANAGERS ? RolesEnums.MANAGER : RolesEnums.EMPLOYEE;
    deliveryChoice.value = choice;
    if (await notify(recipients)) return;

    // The form did not open: leave no branch behind.
    deliveryChoice.value = null;
    createSession.value.choiceDelivery = previousDelivery;
  }

  async function unscheduleUsersFromSession() {
    const feedback = useFeedback();
    const { project } = storeToRefs(createProjectCoreStore);
    const { sessionDetails } = storeToRefs(sessionDetailsStore);
    if (project.value.users.excludedUsersIdList.length > 0 || !project.value.users.selected?.length) {
      if (!sessionDetails?.value?.id || !sessionDetails?.value?.itemType) return;
      const excludedUserIds = sessionAttendeesMultiSelect.value?.excludedItems?.map((item: any) => item.userId) ?? [];
      const selectedAttendees = await sessionDetailsAttendeesStore.getSelectedAttendeesInSession(
        excludedUserIds,
        sessionDetails.value?.id as string,
        sessionDetails.value?.itemType as ItemTypeSDK,
      );
      if (selectedAttendees?.length) {
        project.value.users.selected = selectedAttendees.map((user: any) => ({
          ...user,
          originalId: user.bookingId,
        }));
      } else {
        feedback.error(`erreur`, "small");
      }
    }
    showUnscheduleAttendeesModal.value = true;
  }

  async function deleteAttendeesFromSession() {
    const feedback = useFeedback();
    const { project } = storeToRefs(createProjectCoreStore);
    const { sessionDetails } = storeToRefs(sessionDetailsStore);
    if (project.value?.users?.excludedUsersIdList?.length > 0 || !project.value?.users?.selected?.length) {
      if (!sessionDetails?.value?.id || !sessionDetails?.value?.itemType) return;
      const excludedUserIds = sessionAttendeesMultiSelect.value?.excludedItems?.map((item: any) => item.userId) ?? [];
      const selectedAttendees = await sessionDetailsAttendeesStore.getSelectedAttendeesInSession(
        excludedUserIds,
        sessionDetails.value?.id as string,
        sessionDetails.value?.itemType as ItemTypeSDK,
      );
      if (selectedAttendees?.length) {
        project.value.users.selected = selectedAttendees.map((user: any) => ({
          ...user,
          originalId: user.bookingId,
        }));
      } else {
        feedback.error(`erreur`, "small");
      }
    }
    showDeleteAttendeesModal.value = true;
  }

  function getFootersActionsSessions(): IFooterActions[] {
    return [
      {
        name: "Tag formation",
        logoName: "categorie",
        action: addTrainingTag,
      },
      {
        name: "Tag session",
        logoName: "categorie",
        action: addSessionTag,
      },
      {
        name: "Gérer la visibilité",
        logoName: "see",
        action: showVisibility,
      },
    ];
  }

  return {
    headers,
    sessionHeaders,
    sessionAttendeesHeaders,
    sessionStatusMapping,
    statusMapping: statusMap,
    sessionTypeMapping: sessionTypeMap,
    sessionItemFormatMap,
    getFootersActions,
    notifyDelivery,
    getFootersActionsSessions,
  };
};
